'use client';

import { channelsQueryOptions } from '@/api/options';
import { Button, getRouterPath, ProfileImage } from '@/components';
import {
  channelFollowStatusQueryOption,
  useChannelFollowStatusQuery,
  useFollowChannel,
  useIsLoggedIn,
  useUnfollowChannel,
} from '@/entities';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { useModal } from '@/hooks/useModal';
import webViewService from '@/lib/webViewService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Column, Element, Icon, Row } from '@vuddy/components';
import { getClientSideUserAgent, trackEvent } from '@vuddy/utils';
import { useRouter } from 'next/navigation';
import Skeleton from 'react-loading-skeleton';
import * as styles from './channelInfoSection.css';

interface Props {
  channelUrl: string;
  isGacha?: boolean;
  salePageId: number;
  salePageType: string;
}

export const ChannelInfoSection = ({
  channelUrl,
  isGacha,
  salePageId,
  salePageType,
}: Props) => {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useIsLoggedIn();
  const loginConfirm = useLoginInducingModal();
  const { confirm } = useModal();
  const router = useRouter();

  // 채널 정보 가져오기
  // 채널 id로 가져오는 api는 인증이 필수임.
  const { data: channel, isLoading: isChannelLoading } = useQuery(
    channelsQueryOptions.getDetailByUrlChannelV2({
      payload: { url: channelUrl },
    })
  );

  const { data: isFollowing, isLoading: isFollowingLoading } =
    useChannelFollowStatusQuery(channel?.id, isLoggedIn);

  const { mutate: follow, isPending: followPending } = useFollowChannel();
  const { mutate: unfollow, isPending: unfollowPending } = useUnfollowChannel();

  const invalidateFollowStatus = () => {
    if (!channel?.id) return;
    queryClient.invalidateQueries({
      queryKey: channelFollowStatusQueryOption({
        pathVariables: channel.id,
      }).queryKey,
    });
  };

  const handleFollowToggle = () => {
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }

    if (!channel?.id) return;

    if (isFollowing) {
      confirm({
        title: '팔로우 취소',
        description: `${channel.name} 관련된 소식을 받을 수 없습니다. 그래도 진행 하시겠습니까?`,
        hasCancel: true,
        onConfirm: () => {
          trackEvent('shop', 'unfollow_channel', {
            channel_id: channel.id,
            route: 'sale_page',
          });
          unfollow(
            {
              id: channel.id,
              sale_page_id: salePageId,
              sale_type: salePageType,
              route: 'sale_page',
            },
            { onSuccess: invalidateFollowStatus }
          );
        },
      });
    } else {
      trackEvent('shop', 'follow_channel', {
        channel_id: channel.id,
        route: 'sale_page',
      });
      follow(
        {
          id: channel.id,
          sale_page_id: salePageId,
          sale_type: salePageType,
          route: 'sale_page',
        },
        { onSuccess: invalidateFollowStatus }
      );
    }

    // 웹뷰이면서 iOS일 때 postMessage 호출
    const isAppWebView = webViewService.isAppWebView(getClientSideUserAgent());
    const isIOS =
      webViewService.getDevicePlatform(getClientSideUserAgent()) === 'IOS';

    if (isAppWebView && isIOS) {
      webViewService.postMessage({
        type: 'followChannel',
        data: !isFollowing,
      });
    }
  };

  const handleClickChannel = () => {
    const isAppWebView = webViewService.isAppWebView(getClientSideUserAgent());
    const routerPath = getRouterPath({ isAppWebView });

    trackEvent('shop', 'click_sale_go_to_channel', {
      channel_id: channel?.id,
      channel_name: channel?.name,
      sale_page_id: salePageId,
    });
    router.push(
      routerPath.channelsHome({
        channelUrl,
        channelId: channel?.id,
      })
    );
  };

  if (isChannelLoading || !channel) {
    return (
      <Row
        gap="gap_base"
        alignItems="center"
        justifyContent="space-between"
        paddingY="padding_small">
        <Row gap="gap_small" alignItems="center">
          {/* 프로필 이미지 스켈레톤 */}
          <Skeleton circle width={40} height={40} />

          {/* 채널 정보 스켈레톤 */}
          <Column gap="gap_2xs">
            <Skeleton width={80} height={16} />
            <Skeleton width={120} height={14} />
          </Column>
        </Row>

        {/* 팔로우 버튼 스켈레톤 */}
        <Skeleton width={60} height={28} />
      </Row>
    );
  }

  const isFollowDisabled =
    isFollowingLoading || followPending || unfollowPending;
  const iconName = isFollowing ? 'OUTLINE_CHECK' : 'OUTLINE_PLUS';
  const gachaIconColor = isFollowing ? 'whitePrimary' : 'whitePrimary';
  const restIconColor = isFollowing ? 'secondary' : 'whitePrimary';
  const iconColor = isGacha ? gachaIconColor : restIconColor;

  const gachaButtonVariant = isFollowing ? 'outlineWhite' : 'solidPrimary';
  const restButtonVariant = isFollowing ? 'outlineSecondary' : 'solidPrimary';
  const buttonVariant = isGacha ? gachaButtonVariant : restButtonVariant;

  return (
    <Row
      gap="gap_base"
      alignItems="center"
      justifyContent="space-between"
      paddingBottom="padding_small"
      className={styles.channelInfoSection({ isGacha })}>
      <Row gap="gap_small" alignItems="center">
        {/* 채널 썸네일 */}
        {channel.thumbnail && (
          <ProfileImage url={channel.thumbnail?.url} imageSize="xs" />
        )}

        {/* 채널 정보 */}
        <Row
          gap="gap_2xs"
          color={isGacha ? 'whitePrimary' : 'primary'}
          alignItems="center"
          cursor="pointer"
          onClick={handleClickChannel}>
          <Element typography="bodyXsAccent">{channel.name}</Element>
          <Icon
            name={
              channel.type === 'SECONDARY_CREATOR'
                ? 'FILL_FAN_CREATOR'
                : 'FILL_VERIFIED'
            }
            size="16px"
            color={isGacha ? 'whitePrimary' : 'primary'}
          />
          <Icon name="OUTLINE_CHEVRON_RIGHT" size="16px" />
        </Row>
      </Row>

      {/* 팔로우 버튼 */}
      <Button
        onClick={handleFollowToggle}
        disabled={isFollowDisabled}
        variant={buttonVariant}
        size="xs">
        <Row gap="gap_2xs" alignItems="center">
          <Icon name={iconName} size="12px" color={iconColor} />

          {followPending || unfollowPending
            ? '처리 중...'
            : isFollowing
              ? '팔로잉'
              : '팔로우'}
        </Row>
      </Button>
    </Row>
  );
};
